/**
 * Whether the shell may render at all: the organization gate.
 *
 * Ported from `bimgo-ai-web`'s two blocks at the top of `pages/Projects.tsx` —
 * "no organization" and "subscription expired" — with two deliberate changes,
 * both forced by 2.5 being a desktop app rather than a page in a browser.
 *
 * **1. It gates the shell, not one pane.** v2 put both checks at the top of the
 * Projects page, so Settings and every other route stayed reachable with a dead
 * subscription. 2.5 also opens on `threads`, not `projects` (`routes.ts`), so
 * porting the placement literally would have left the gate on a pane the user
 * never has to open.
 *
 * **2. The decision is a pure function**, separate from the view. The states are
 * only distinguishable by data that arrives asynchronously, and the failure mode
 * that matters — a gate that flashes over a perfectly good session while the org
 * list is still in flight — is a timing bug no screenshot catches. Pure and
 * exported means it can be tested at every ordering without a browser.
 *
 * **`loaded` is not `organizations.length > 0`.** Those are different questions,
 * and conflating them is what produces the flash: before the fetch resolves the
 * list is *also* empty, and "empty because we have not asked yet" must read as
 * "wait", never as "you belong to no organization".
 */

/** The subset of `Organization` this decision needs — see `organizations.ts`. */
export interface GateOrganization {
  id: string;
  name: string;
  /**
   * Null when the org has no valid subscription **right now** — expired,
   * disabled, or never subscribed. api-v2 collapses all three into null, which
   * is why one check covers them and why this never reads `status` or `ends_at`
   * to second-guess it.
   */
  active_subscription: unknown | null;
}

export type OrgGate =
  | { kind: "ok" }
  | { kind: "no-organization" }
  | { kind: "subscription-expired"; organizationName: string };

/**
 * Which org the shell is working on: the selected one, else the first.
 *
 * The same fallback `AppShell` uses for `currentOrg`. Exported so the gate and
 * the shell cannot drift apart — a gate that judged a different org from the one
 * the panes read would block on one org's lapse while showing another's work.
 */
export function currentOrganization(
  organizations: readonly GateOrganization[],
  currentOrgId: string | null,
): GateOrganization | null {
  return organizations.find((o) => o.id === currentOrgId) ?? organizations[0] ?? null;
}

export function orgGate(input: {
  /** Has the org list actually come back? Not "is it non-empty". */
  loaded: boolean;
  organizations: readonly GateOrganization[];
  currentOrgId: string | null;
}): OrgGate {
  // Nothing is known yet. The shell renders its own loading states per pane;
  // blocking here would replace them with a red card that then vanishes.
  if (!input.loaded) return { kind: "ok" };

  if (input.organizations.length === 0) return { kind: "no-organization" };

  const current = currentOrganization(input.organizations, input.currentOrgId);
  // Unreachable given the length check above, kept so a future caller passing a
  // `currentOrgId` from somewhere else cannot turn this into a crash.
  if (!current) return { kind: "no-organization" };

  if (current.active_subscription === null || current.active_subscription === undefined) {
    return { kind: "subscription-expired", organizationName: current.name };
  }

  return { kind: "ok" };
}

/**
 * Does this user have somewhere else to go?
 *
 * Drives whether the expired card tells them to switch organizations. A user in
 * one lapsed org and a user in two, one of which still works, are looking at the
 * same wall with different ways round it — and telling the first to "switch
 * organization" when there is nothing to switch to is worse than saying nothing.
 */
export function hasAnotherValidOrganization(
  organizations: readonly GateOrganization[],
  currentOrgId: string | null,
): boolean {
  const current = currentOrganization(organizations, currentOrgId);
  return organizations.some(
    (o) => o.id !== current?.id && o.active_subscription !== null && o.active_subscription !== undefined,
  );
}
